import { Event } from '@amzn/ring-neighbors-api-orchestrator-gql-schema';
import { DeniedPostPage } from '@amzn/ring-neighbors-contentful-gql-schema';
import {
  CalendarTimelineDefaultIcon,
  CheckIcon,
  DisableCircleIcon,
  Page,
  PageBody,
  QuestionmarkCircleIcon,
  SidebarNavigation,
  SidebarNavigationCell,
  SidebarNavigationHeader,
  SidebarNavigationSection,
  PostNeighborhoodsIcon,
} from '@amzn/ring-safe-web';
import { Box, Flex } from '@amzn/ring-ui-react-components';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsNpssUser } from 'src/auth';
import {
  useNeighborsWebClickEventStreamDispatcher,
  useNeighborsWebViewEventStreamDispatcher,
} from 'src/components/App/hooks';
import { useAuth } from 'src/context';
import { useGetNpssPublishedPosts } from 'src/features/MyPosts/hooks/useGetNpssPublishedPosts';
import { useGetNpssScheduledPosts } from 'src/features/MyPosts/hooks/useGetNpssScheduledPosts';
import { usePushToFeed, useSearchParam } from 'src/hooks';
import { usePushToHistory } from 'src/hooks/usePushToHistory';
import { AppRoutes } from 'src/routes';
import { BackButton } from 'src/shared/components/BackButton';
import { useEventSchedulePostUpdate } from 'src/shared/components/EventCard/hooks/useEventSchedulePostUpdate';
import { CenteredLoading } from 'src/shared/components/Loading';
import { SubNavbar } from 'src/shared/components/SubNavbar';
import { toast } from 'src/shared/components/Toast';
import { useIsMobile, useRouteTracker } from 'src/shared/hooks';
import { PostStatus } from 'src/types';
import {
  addStatusToEvents,
  convertNpssScheduledPostToEventAndAddStatus,
  formatCreatedDate,
  ScheduledEvent,
  sortEventsDescendingByDateCreated,
  sortScheduledEventsAscendingByScheduledFor,
} from 'src/utils/events';
import { UnifiedMyPosts } from './UnifiedMyPosts';
import { useGetDeniedPostsPages } from './hooks/useGetDeniedPostsPages';
import { useGetUserEventsHistory } from './useGetUserEventsHistory';
import './navbar.css';

export const UnifiedMyPostsPage = () => {
  const { t } = useTranslation(['features.myPosts', 'components.navbar']);
  const pushToFeed = usePushToFeed();
  const pushToEventCreate = usePushToHistory(AppRoutes.EventCreate);
  const { data, loading } = useGetUserEventsHistory();
  const { data: denialReasons } = useGetDeniedPostsPages();
  const {
    data: npssScheduledPosts,
    loading: loadingNpssScheduledPosts,
    refetch: scheduledPostsRefetch,
  } = useGetNpssScheduledPosts();
  const { data: npssPublishedPosts, loading: loadingNpssPublishedPosts } =
    useGetNpssPublishedPosts();
  const pageViewRef = useRef(false);
  const dispatchView =
    useNeighborsWebViewEventStreamDispatcher('nhWeb_myPosts');
  const { isPending } = useAuth();
  const isMobile = useIsMobile() || false;
  const [activeItem, setActiveItem] = useState(0);
  const [isNavbarHidden, setIsNavbarHidden] = useState(false);
  const isNpssUser = useIsNpssUser();
  const postTypeParam = useSearchParam('type');
  const { storeLocation } = useRouteTracker();

  useEffect(() => {
    setActiveItem(postTypeParam === 'scheduled' ? 1 : 0);
  }, [postTypeParam]);

  useEffect(() => {
    storeLocation();
  }, [storeLocation]);

  const handleOnCompleted = () => {
    toast.success(t('update-schedule-post-toast.success'));
  };

  const handleOnError = () => {
    toast.error(t('update-schedule-post-toast.error'));
  };

  const updateScheduledPost = useEventSchedulePostUpdate(
    handleOnCompleted,
    handleOnError,
  );

  const handleReScheduledPostClick = (id: string, scheduledFor: string) => {
    updateScheduledPost({ id, scheduled_for: scheduledFor });
  };

  const handleEditScheduledPostClick = (
    id: string,
    title: string,
    description: string,
  ) => {
    updateScheduledPost({ id, title, description });
  };

  const handleClick = (n: number) => {
    setActiveItem(n);
    if (isMobile) {
      setIsNavbarHidden(true);
    }
  };

  useEffect(() => {
    if (pageViewRef.current || isPending) return;

    dispatchView();
    pageViewRef.current = true;
  }, [dispatchView, isPending]);

  useEffect(() => {
    setIsNavbarHidden(isMobile);
  }, [isMobile]);

  const dispatchClick =
    useNeighborsWebClickEventStreamDispatcher('nhWeb_myPosts');

  const handleResolvePostClick = () => {
    if (dispatchClick) {
      dispatchClick('nhWeb_resolvePost', ['Neighbors Web']);
    }
  };

  const handleNoPostsClick = () => {
    if (dispatchClick) {
      dispatchClick('nhWeb_createPost', undefined, {
        itemType: 'button',
      });
    }
    pushToEventCreate();
  };

  // Fires when a post is deleted
  const handleMutationCompletion = () => {
    scheduledPostsRefetch();
  };

  const { approved = [], pending = [], rejected = [] } = data;

  const pendingEvents = addStatusToEvents(pending, PostStatus.PENDING);
  const approvedEvents = addStatusToEvents(approved, PostStatus.APPROVED);
  const rejectedEvents = addStatusToEvents(rejected, PostStatus.DENIAL);

  const scheduledEvents = sortScheduledEventsAscendingByScheduledFor(
    convertNpssScheduledPostToEventAndAddStatus(
      npssScheduledPosts,
      PostStatus.SCHEDULED,
    ) as ScheduledEvent[],
  );

  const publishedEvents = sortEventsDescendingByDateCreated(
    addStatusToEvents(
      formatCreatedDate(npssPublishedPosts as unknown[] as Event[]),
      PostStatus.PUBLISHED,
    ),
  );

  const sidebarNavigationCells = [
    {
      key: 'active-posts',
      title: t('active-posts'),
      icon: <CheckIcon size={32} />,
      dataTestId: 'sidenav-active-posts',
      isNpssItem: false,
    },
    {
      key: 'pending-posts',
      title: t('posts-in-review'),
      icon: <QuestionmarkCircleIcon size={32} />,
      dataTestId: 'sidenav-pending-posts',
      isNpssItem: false,
    },
    {
      key: 'denied-posts',
      title: t('denied-posts'),
      icon: <DisableCircleIcon size={32} />,
      dataTestId: 'sidenav-denied-posts',
      isNpssItem: false,
    },
    {
      key: 'published-posts',
      title: t('published-posts'),
      icon: <CheckIcon size={32} />,
      dataTestId: 'sidenav-npss-published-posts',
      isNpssItem: true,
    },
    {
      key: 'scheduled-posts',
      title: t('scheduled-posts'),
      icon: <CalendarTimelineDefaultIcon size={32} />,
      dataTestId: 'sidenav-npss-scheduled-posts',
      isNpssItem: true,
    },
  ];

  return (
    <>
      {loading ||
      loadingNpssScheduledPosts ||
      loadingNpssPublishedPosts ? null : (
        <SubNavbar>
          <BackButton
            onClick={
              isMobile && isNavbarHidden
                ? () => setIsNavbarHidden(false)
                : pushToFeed
            }
            w="auto"
          >
            {t('back')!}
          </BackButton>
        </SubNavbar>
      )}
      <Page
        style={{ display: 'flex', flex: 1, overflow: 'hidden' }}
        data-testid="my-posts-container"
      >
        <SidebarNavigation
          className={isNavbarHidden ? 'hide-navbar' : ''}
          data-testid="sidebar-navigation"
        >
          <SidebarNavigationHeader
            icon={<PostNeighborhoodsIcon />}
            title={t('my-posts', { ns: 'components.navbar' })!}
          />
          <SidebarNavigationSection title="">
            {sidebarNavigationCells
              .filter((cell) => cell.isNpssItem === isNpssUser)
              .map((cell, index) => (
                <SidebarNavigationCell
                  key={cell.key}
                  checked={activeItem === index}
                  onClick={() => handleClick(index)}
                  image={cell.icon}
                  title={cell.title}
                  data-testid={cell.dataTestId}
                >
                  {/* Empty */}
                </SidebarNavigationCell>
              ))}
          </SidebarNavigationSection>
        </SidebarNavigation>
        <PageBody data-testid="page-content">
          <Flex
            direction="column"
            flex={1}
            maxWidth={{ m: 603 }}
            width="100%"
            mx="auto"
            px={{ base: 6, m: 0 }}
            py={7}
          >
            {loading ||
            loadingNpssScheduledPosts ||
            loadingNpssPublishedPosts ? (
              <CenteredLoading />
            ) : (
              <Box position="relative">
                {activeItem === 0 &&
                  (!isNpssUser ? (
                    <UnifiedMyPosts
                      events={approvedEvents}
                      handleNoPostsClick={handleNoPostsClick}
                      handleResolvePostClick={handleResolvePostClick}
                      dataTestId="active-posts"
                    />
                  ) : (
                    <UnifiedMyPosts
                      events={publishedEvents}
                      handleNoPostsClick={handleNoPostsClick}
                      handleResolvePostClick={handleResolvePostClick}
                      dataTestId="published-posts"
                    />
                  ))}
                {activeItem === 1 &&
                  (!isNpssUser ? (
                    <UnifiedMyPosts
                      events={pendingEvents}
                      handleNoPostsClick={handleNoPostsClick}
                      handleResolvePostClick={handleResolvePostClick}
                      dataTestId="pending-posts"
                    />
                  ) : (
                    <UnifiedMyPosts
                      events={scheduledEvents}
                      handleNoPostsClick={handleNoPostsClick}
                      handleResolvePostClick={handleResolvePostClick}
                      handleReSchedulePostClick={handleReScheduledPostClick}
                      mutationHasFinished={handleMutationCompletion}
                      handleEditScheduledPostClick={
                        handleEditScheduledPostClick
                      }
                      dataTestId="scheduled-posts"
                    />
                  ))}
                {activeItem === 2 && !isNpssUser && (
                  <UnifiedMyPosts
                    events={rejectedEvents}
                    handleNoPostsClick={handleNoPostsClick}
                    handleResolvePostClick={handleResolvePostClick}
                    dataTestId="rejected-posts"
                    denialReasons={denialReasons as DeniedPostPage[]}
                  />
                )}
              </Box>
            )}
          </Flex>
        </PageBody>
      </Page>
    </>
  );
};
